import ReceiptImage from '@/components/Receipt/ReceiptImage';
import { useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState } from '@/store';
import { IReceipt } from '@/types/common.type';
import { comma } from '@/utils/format.util';
import { getBillSliderSetting } from '@repo/constants';
import { FRANCHISEE_AIRPORT_ITEM } from '@repo/types';
import { Icon } from '@successmode-ktp/kui';
import { format } from 'date-fns';
import { useRef, useState } from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';

type Props = {
  billList: IReceipt[] | FRANCHISEE_AIRPORT_ITEM[];
  openedId: number;
  onClose: () => void;
};

const DetailBillModal = ({ billList, openedId, onClose }: Props) => {
  const ModalRef = useRef(null);
  const { passportNumber } = useAppSelector((state) => state) as IGlobalState;
  const [nowPage, setNowPage] = useState(openedId);

  const getBillData = (info: IReceipt | FRANCHISEE_AIRPORT_ITEM) => {
    return {
      purchaseSn: '2022',
      refundAfter: false,
      barcodeS3Path: null,
      rowDataArr: [
        {
          title: '지정번호',
          subTitle: 'Certificate No.',
          content: info.taxFreeStoreNumber,
        },
        {
          title: '판매일',
          subTitle: 'Date of Sale',
          content: format(new Date(info.saleDate), 'yyyy.MM.dd HH:mm:SS'),
        },
        {
          title: '판매자',
          subTitle: 'Retailer',
          content: info.sellerName,
        },
        {
          title: '상호',
          subTitle: 'Name of Company',
          content: info.franchiseeName,
        },
        {
          title: '사업자번호',
          subTitle: 'Reg. No.',
          content: info.businessNumber,
        },
        {
          title: '주소',
          subTitle: 'Address',
          content: info.storeAddress,
        },
        {
          title: '대표자 / 연락처',
          subTitle: 'Address',
          content: `${info.sellerName} / ${info.storeTelNumber}`,
          isLast: true,
        },
        {
          title: '물품구매내역',
          subTitle: 'Description of Goods',
          content: '',
        },
        {
          title: '',
          subTitle: '단가',
          hasComma: true,
          content: comma(info.totalAmount),
        },
        {
          title: '',
          subTitle: '수량',
          content: '01',
        },
        {
          title: '',
          subTitle: '금액',
          hasComma: true,
          content: comma(info.totalAmount),
        },
        {
          title: '판매총액',
          subTitle: 'Total Payment',
          hasComma: true,
          content: comma(info.totalAmount),
        },
        {
          title: '부가가치세',
          subTitle: 'V.A.T',
          hasComma: true,
          content: comma(info.totalVat),
        },
        {
          title: '환급액',
        },
        {
          title: '(송금세액)',
          subTitle: 'Net Refund',
        },
        {
          title: '',
          subTitle: 'Value',
          hasComma: true,
          content: comma(info.totalRefund),
        },
        {
          title: '반출유효기간',
          subTitle: 'Export Expiry',
        },
        {
          title: '',
          subTitle: 'Date',
          content: info.expireDate,
          isLast: true,
        },
        {
          title: '구매자',
          subTitle: 'Customer in Passport',
        },
        {
          title: '여권번호',
          subTitle: 'Passport No.',
          content: passportNumber,
        },
        { title: '성명', subTitle: 'Passport Name', content: '' },
        { title: '국적', subTitle: 'Nationality', content: '' },
        { title: '주소', subTitle: 'Address for Refund', content: '' },
        { title: '', subTitle: '', content: '' },
        { title: '서명', subTitle: 'Signature' },
        {
          info: 'I agree above info is used only for Refund',
        },
        {
          title: '세관반출확인',
          subTitle: 'Stamp For Verification Of Export',
        },
      ],
    };
  };

  const setting = getBillSliderSetting({
    length: billList.length,
    showIndex: openedId,
  });

  return (
    <>
      <Modal isOpen={openedId > -1} ref={ModalRef}>
        <StyledSlider
          {...setting}
          afterChange={(v) => {
            setNowPage(v);
          }}
          nextArrow={
            <NextButton $disabled={billList.length === nowPage + 1}>
              <Icon.ChevronRight
                size={24}
                fillColor={billList.length === nowPage + 1 ? '#999' : '#111'}
              />
            </NextButton>
          }
          prevArrow={
            <PrevButton $disabled={nowPage === 0}>
              <Icon.ChevronLeft
                size={24}
                fillColor={nowPage === 0 ? '#999' : '#111'}
              />
            </PrevButton>
          }
        >
          {billList.map((info, index) => (
            <ReceiptImage
              key={index}
              info={getBillData(info)}
              index={index}
              serialNumber={info.purchaseSn}
              onClose={onClose}
            />
          ))}
        </StyledSlider>
      </Modal>
      {openedId > -1 && <Backdrop />}
    </>
  );
};

const Modal = styled.aside<{ isOpen: boolean }>`
  position: fixed;
  top: ${({ isOpen }) => (isOpen ? '50%' : '100%')};
  left: 50%;
  transform: ${({ isOpen }) =>
    isOpen ? 'translate(-50%, -50%)' : 'translateX(-50%)'};
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: transparent;
  z-index: 200;
  transition: top 400ms;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Backdrop = styled.aside`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  opacity: 0.4;
  z-index: 100;
  overflow: hidden;
`;

const StyledSlider = styled(Slider)`
  position: relative;
  width: 335px;
  height: auto;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ArrowButton = styled.button`
  position: fixed;
  top: 65%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 100px;
  filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.15));
  background-color: #fff !important;
  z-index: 300;

  &::before {
    content: '';
  }

  &:hover {
    opacity: 1 !important;
  }
`;

const PrevButton = styled(ArrowButton)<{ $disabled: boolean }>`
  left: 10px;
  background-color: ${({ $disabled }) =>
    $disabled ? '#E5E6E8' : '#fff'} !important;
`;

const NextButton = styled(ArrowButton)<{ $disabled: boolean }>`
  right: 10px;
  background-color: ${({ $disabled }) =>
    $disabled ? '#E5E6E8' : '#fff'} !important;
`;

export default DetailBillModal;
